/*
 * -----------------------------------------------------------------------
 *  <copyright file="update-account-dialog.tsx" company="Triumph Research Intelligence">
 *      Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *  </copyright>
 * -----------------------------------------------------------------------
 */

import React, { useContext, useEffect, useState } from 'react';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { pick } from 'lodash';
import { UserContext } from 'contexts/user-context';
import { useBoolean, useTranslation } from 'hooks';
import CustomPhoneInput from 'views/components/core/phone-input';

import './styles.scss';
import {
    UPDATE_USER_EMAIL,
    UPDATE_USER_NAME,
    UPDATE_USER_PASSWORD,
    UPDATE_USER_PHONE,
    GET_ORGANISATION,
    GET_SYSTEM_USERS,
} from 'queries/uam';
import doLogout from 'utils/auth.service';
import Loading from 'views/components/core/loading';
import Constants from 'utils/constants';
import { validateFields } from 'utils/validation';
import { SaveButton } from 'views/components/core/buttons';
import CharactersRemaining from 'views/components/core/characters-remaining';
import { CustomDialog, DialogActions, DialogContent, DialogTitle } from 'views/components/core/dialog';
import IconButton from 'views/components/core/icon-button';
import InputAdornment from 'views/components/core/input-adornment';
import PasswordRequirementsWarning from 'views/components/core/password-requirements-warning';
import TextField from 'views/components/core/text-field';

const UpdateAccountDialog = ({
    dialogTitle,
    open,
    onClose,
    user,
    dialogType,
    onAccountUpdate,
}: {
    dialogTitle: string;
    open: boolean;
    onClose: Function;
    user: any;
    dialogType: string;
    onAccountUpdate: Function;
}) => {
    const client = useApolloClient();
    const userContext = useContext(UserContext);

    const initUserData = user;
    const { t } = useTranslation();
    const [errors, setErrors] = useState({} as any);
    const [failedAttempts, setFailedAttempts] = useState(0);
    const [account, setAccount] = useState(user);
    const [passwords, setPasswords] = useState({
        currentPassword: '' as string,
        newPassword: '' as string,
        confirmedPassword: '' as string,
    } as any);
    const [phoneError, setPhoneError] = useState(null);
    const showPassword = useBoolean(false);
    const showNewPassword = useBoolean(false);
    const showConfirmedPassword = useBoolean(false);
    const passwordIsInvalid = useBoolean(false);
    const { data } = useQuery(GET_ORGANISATION);
    const [loadSystemUsers, { data: systemUsersData, loading }] = useLazyQuery(GET_SYSTEM_USERS);
    const disabledSave = useBoolean(true);
    const saving = useBoolean(false);

    const [updateUserName] = useMutation(UPDATE_USER_NAME);
    const [updateUserPhone] = useMutation(UPDATE_USER_PHONE);
    const [updateUserEmail] = useMutation(UPDATE_USER_EMAIL);
    const [updateUserPassword] = useMutation(UPDATE_USER_PASSWORD);

    useEffect(() => {
        setAccount({ ...user });
    }, [user]);

    useEffect(() => {
        if (open && userContext?.user.systemAdminLevel > 0 && !systemUsersData) loadSystemUsers();
    }, [userContext, open]);

    useEffect(() => {
        if (
            (data?.organisation &&
                data.organisation.security.numAttemptsBeforeLockout === failedAttempts &&
                user.systemAdminLevel <= 0) ||
            (user.systemAdminLevel > 0 && failedAttempts === 3)
        )
            doLogout(client, userContext, 'MaxAttemptsFailed');
    }, [failedAttempts, data]);

    const close = (action: string | boolean | undefined, errorMessage?: string) => {
        disabledSave.setTrue();
        if (action !== 'error' && action !== false) {
            disabledSave.setTrue();
            setPasswords({
                currentPassword: '' as string,
                newPassword: '' as string,
                confirmedPassword: '' as string,
            });
            onAccountUpdate(account);
        } else if (action === false) {
            disabledSave.setTrue();
            setPasswords({
                currentPassword: '' as string,
                newPassword: '' as string,
                confirmedPassword: '' as string,
            });
            setAccount({
                ...initUserData,
            });
        }
        onClose(action, errorMessage);
        setErrors({});
    };

    const validate = (value?: string, field?: string, length?: number, required?: boolean) => {
        const validateError = validateFields(value, field, length, required);

        if (field === 'newPassword' || field === 'confirmedPassword')
            setErrors({
                ...errors,
                newPassword: null,
                confirmedPassword: null,
                ...validateError,
            });
        else
            setErrors({
                ...errors,
                ...validateError,
            });
    };

    const onChange =
        (name: string, length: number = 60) =>
        (event: any) => {
            const value = event.target.value.trim();
            disabledSave.setFalse();
            if (name === 'currentPassword')
                setPasswords({
                    ...passwords,
                    currentPassword: value,
                });
            if (dialogType === 'password') {
                if (name === 'newPassword')
                    setPasswords({
                        ...passwords,
                        newPassword: value,
                    });
                else if (name === 'confirmedPassword')
                    setPasswords({
                        ...passwords,
                        confirmedPassword: value,
                    });
            } else
                setAccount({
                    ...account,
                    [name]: event.target.value,
                });

            validate(value, name, length, false);
        };

    const checkIfEmailExists = () => {
        if (userContext.user.systemAdminLevel > 0) {
            const ixExists =
                systemUsersData.systemUsers.findIndex(
                    (item: any) => item.email === account.email && item.id !== userContext.user.id,
                ) >= 0;
            if (ixExists) close('error', t('uam:emailAlreadyInUseTRI'));
            return ixExists;
        }
        const ixExists =
            userContext.user.organisation.members.findIndex(
                (item: any) => item.email === account.email && item.userId !== user.id,
            ) >= 0;
        if (ixExists) close('error', t('uam:emailAlreadyInUse'));
        return ixExists;
    };

    const validateOnUpdate = () => {
        const localErrors: any = {};
        const errorCurrentPassword = validateFields(passwords.currentPassword, 'currentPassword', 60, true);

        localErrors.currentPassword = errorCurrentPassword.currentPassword;

        if (dialogType === 'name') {
            const errorFirstName = validateFields(account.firstName, 'firstName', 50, true);
            const errorLastName = validateFields(account.lastName, 'lastName', 50, true);

            localErrors.firstName = errorFirstName.firstName;
            localErrors.lastName = errorLastName.lastName;
        } else if (dialogType === 'phone') {
            const errorPhone = validateFields(account.phone, 'phone', 16, true);

            localErrors.phone = errorPhone.phone;
            if (!localErrors.phone && phoneError) localErrors.phone = phoneError;
        } else if (dialogType === 'email') {
            if (checkIfEmailExists()) {
                return true;
            }
            const errorEmail = validateFields(account.email, 'email', 320, true);

            localErrors.email = errorEmail.email;

            if (!localErrors.email) {
                const emailReg =
                    /^[\W]*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4}[\W]*;{1}[\W]*)*([\w+\-.%]+@[\w\-.]+\.[A-Za-z]{2,4})[\W]*$/;
                if (!emailReg.test(account.email)) {
                    localErrors.email = t('uam:incorrectEmailFormat');
                }
            }
        } else if (dialogType === 'password') {
            const errorNewPassword = validateFields(passwords.newPassword, 'newPassword', 60, true);
            const errorConfirmedPassword = validateFields(passwords.confirmedPassword, 'confirmedPassword', 60, true);

            localErrors.newPassword = errorNewPassword.newPassword;
            localErrors.confirmedPassword = errorConfirmedPassword.confirmedPassword;

            if (!localErrors.newPassword && !localErrors.confirmedPassword) {
                if (passwordIsInvalid.value) {
                    localErrors.newPassword = t('uam:incorrectPasswordFormat');
                    if (passwords.confirmedPassword.length)
                        localErrors.confirmedPassword = t('uam:incorrectPasswordFormat');
                } else if (
                    passwords.newPassword !== passwords.confirmedPassword &&
                    passwords.confirmedPassword.length
                ) {
                    localErrors.newPassword = t('uam:passwordsDontMatch');
                    localErrors.confirmedPassword = t('uam:passwordsDontMatch');
                }
            }
        }

        setErrors({
            ...errors,
            ...localErrors,
        });

        return Object.keys(localErrors).filter((item) => localErrors[item]).length > 0;
    };

    const updateProfileData = () => {
        const updatedValues: any = {
            currentPassword: passwords.currentPassword,
            newFirstName: account.firstName,
            newLastName: account.lastName,
            newPhone: account.phone,
            newEmail: account.email,
            newPassword: passwords.confirmedPassword,
        };
        saving.setTrue();
        if (dialogType === 'name') {
            const newData = pick(updatedValues, ['currentPassword', 'newFirstName', 'newLastName']);
            updateUserName({
                variables: {
                    ...newData,
                    reason: '',
                },
            })
                .then((res: any) => {
                    saving.setFalse();
                    if (res.data.updateUserName === null) close('update');
                    else {
                        if (res?.data?.updateUserName.match('invalidPassword')) {
                            setFailedAttempts(failedAttempts + 1);
                            setErrors({
                                ...errors,
                                currentPassword: t('uam:incorrectPassword'),
                            });
                        } else close('error', t('uam:unableUpdateUserProfileName'));
                        disabledSave.setTrue();
                    }
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        } else if (dialogType === 'phone') {
            const newData = pick(updatedValues, ['currentPassword', 'newPhone']);
            updateUserPhone({
                variables: {
                    ...newData,
                    reason: '',
                },
            })
                .then((res: any) => {
                    saving.setFalse();
                    if (res.data.updateUserPhone === null) {
                        if (!data.organisation.security.twoFactorEnabled && user.systemAdminLevel === 0)
                            close('update');
                    } else if (res?.data?.updateUserPhone.match('invalidPassword')) {
                        setFailedAttempts(failedAttempts + 1);
                        setErrors({
                            ...errors,
                            currentPassword: t('uam:incorrectPassword'),
                        });
                    } else close('error', t('uam:unableUpdateUserProfileMobile'));
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        } else if (dialogType === 'email') {
            const newData = pick(updatedValues, ['currentPassword', 'newEmail']);
            updateUserEmail({
                variables: {
                    ...newData,
                    reason: '',
                },
            })
                .then((res: any) => {
                    saving.setFalse();
                    if (res.data.updateUserEmail === null) {
                        close('update');
                        doLogout(client, userContext, 'EmailVerification');
                    } else if (res?.data?.updateUserEmail.match('invalidPassword')) {
                        setFailedAttempts(failedAttempts + 1);
                        setErrors({
                            ...errors,
                            currentPassword: t('uam:incorrectPassword'),
                        });
                    } else close('error', t('uam:unableUpdateUserProfileEmail'));
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        } else if (dialogType === 'password') {
            const newData = pick(updatedValues, ['currentPassword', 'newPassword']);
            updateUserPassword({
                variables: {
                    ...newData,
                    reason: '',
                },
            })
                .then((res: any) => {
                    saving.setFalse();
                    if (res.data.updateUserPassword === null) {
                        close('update');
                        doLogout(client, userContext, 'UpdateUserPassword');
                    } else {
                        setFailedAttempts(failedAttempts + 1);
                        if (res.data.updateUserPassword.match('invalidPassword'))
                            setErrors({
                                ...errors,
                                currentPassword: t('uam:incorrectPassword'),
                            });
                        else close('error', t('uam:unableUpdateUserProfilePass'));
                    }
                })
                .catch(() => {
                    saving.setFalse();
                    close('error');
                });
        }
    };

    const onUpdate = () => {
        const hasErrors = validateOnUpdate();

        if (!hasErrors) updateProfileData();
    };

    if (loading) return <Loading float />;

    return (
        <CustomDialog
            className="account-update-dialog"
            onClose={() => close(false)}
            fullWidth
            scroll="paper"
            maxWidth="sm"
            open={open}
        >
            <DialogTitle id="update-account-modal-title">
                {dialogTitle}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => close(false)}
                    data-testid="close-dialog"
                />
            </DialogTitle>
            <DialogContent className="add-risk-dialog-content" style={{ padding: 15 }}>
                {dialogType === 'name' && (
                    <>
                        <TextField
                            className="mb-20"
                            id="account-first-name"
                            label={`${t('common:firstName')}*`}
                            value={account.firstName}
                            placeholder={`${t('uam:enterAFirstName')}*`}
                            fullWidth
                            error={!!errors.firstName}
                            variant="outlined"
                            onChange={onChange('firstName', 50)}
                            helperText={
                                <CharactersRemaining
                                    id="first-name-text"
                                    length={account.firstName.length}
                                    max={50}
                                    error={errors.firstName}
                                />
                            }
                        />
                        <TextField
                            className="mb-20"
                            id="account-last-name"
                            label={`${t('common:lastName')}*`}
                            value={account.lastName}
                            fullWidth
                            error={!!errors.lastName}
                            placeholder={`${t('uam:enterALastName')}*`}
                            variant="outlined"
                            onChange={onChange('lastName', 50)}
                            helperText={
                                <CharactersRemaining
                                    id="last-name-text"
                                    length={account.lastName.length}
                                    max={50}
                                    error={errors.lastName}
                                />
                            }
                        />
                    </>
                )}
                {dialogType === 'phone' && (
                    <div className="account-update-phone margin-b">
                        <CustomPhoneInput
                            customId="account-phone"
                            phone={account.phone || ''}
                            customClassName={errors.phone ? 'error' : ''}
                            isValid={(value: string | null) => {
                                setPhoneError(value);
                            }}
                            error={errors.phone}
                            required
                            label={`${t('uam:phone')}*`}
                            onChange={(event: string | null | undefined) => {
                                const newEvent = { target: { value: event } };
                                onChange('phone')(newEvent);
                            }}
                        />
                    </div>
                )}
                {dialogType === 'email' && (
                    <TextField
                        className="mb-20"
                        id="account-email"
                        label={`${t('common:emailAddress')}*`}
                        value={account.email}
                        placeholder={`${t('common:enterYourEmail')}*`}
                        fullWidth
                        variant="outlined"
                        error={!!errors.email}
                        onChange={onChange('email', 320)}
                        helperText={
                            <CharactersRemaining
                                id="email-text"
                                length={account.email.length}
                                max={320}
                                error={errors.email}
                            />
                        }
                    />
                )}

                {dialogType === 'password' && (
                    <div className="margin-b">
                        <PasswordRequirementsWarning
                            security={
                                user.systemAdminLevel > 0 ? Constants.TRISecuritySettings : data.organisation?.security
                            }
                            password={passwords.newPassword}
                            onPasswordValidate={(value: any) => passwordIsInvalid.setValue(value)}
                        />
                    </div>
                )}

                <TextField
                    className="margin-b"
                    error={errors.currentPassword}
                    label={dialogType === 'password' ? `${t('uam:currentPassword')}*` : `${t('password')}*`}
                    id="password"
                    type={showPassword.value ? 'text' : 'password'}
                    rows={1}
                    fullWidth
                    value={passwords.currentPassword}
                    onChange={onChange('currentPassword')}
                    placeholder={t('uam:enterYourCurrentPassword')}
                    variant="outlined"
                    color="secondary"
                    helperText={
                        errors.currentPassword ? (
                            <CharactersRemaining id="current-pass-text" error={errors.currentPassword} />
                        ) : null
                    }
                    slotProps={{
                        input: {
                            autoComplete: 'new-password',
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        id="toggle-password-visibility"
                                        aria-label={t('togglePasswordVisibility')}
                                        onClick={showPassword.toggle}
                                        onMouseDown={(e: any) => e.preventDefault()}
                                    >
                                        {showPassword.value ? <Visibility /> : <VisibilityOff />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {dialogType === 'password' && (
                    <>
                        <TextField
                            className="margin-b"
                            error={errors.newPassword}
                            label={`${t('uam:newPassword')}*`}
                            id="new-password"
                            type={showNewPassword.value ? 'text' : 'password'}
                            rows={1}
                            fullWidth
                            value={passwords.newPassword}
                            onChange={onChange('newPassword')}
                            placeholder={t('uam:enterYourNewPassword')}
                            variant="outlined"
                            color="secondary"
                            helperText={
                                errors.newPassword ? (
                                    <CharactersRemaining id="new-password-text" error={errors.newPassword} />
                                ) : null
                            }
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                id="toggle-password-visibility"
                                                aria-label={t('togglePasswordVisibility')}
                                                onClick={showNewPassword.toggle}
                                                onMouseDown={(e: any) => e.preventDefault()}
                                            >
                                                {showNewPassword.value ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <TextField
                            className="margin-b"
                            error={errors.confirmedPassword}
                            label={`${t('uam:confirmPassword')}*`}
                            id="confirm-new-password"
                            type={showConfirmedPassword.value ? 'text' : 'password'}
                            rows={1}
                            fullWidth
                            value={passwords.confirmedPassword}
                            onChange={onChange('confirmedPassword')}
                            placeholder={t('uam:confirmYourNewPassword')}
                            variant="outlined"
                            color="secondary"
                            helperText={
                                errors.confirmedPassword ? (
                                    <CharactersRemaining
                                        id="confirmed-password-text"
                                        error={errors.confirmedPassword}
                                    />
                                ) : null
                            }
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                id="toggle-password-visibility"
                                                aria-label={t('togglePasswordVisibility')}
                                                onClick={showConfirmedPassword.toggle}
                                                onMouseDown={(e: any) => e.preventDefault()}
                                            >
                                                {showConfirmedPassword.value ? <Visibility /> : <VisibilityOff />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                    </>
                )}
                <DialogActions style={{ padding: '10px 0' }}>
                    <SaveButton
                        disabled={
                            Object.keys(errors).filter((item) => errors[item]).length > 0 ||
                            disabledSave.value ||
                            saving.value
                        }
                        text={t('common:update')}
                        onClick={onUpdate}
                    />
                </DialogActions>
            </DialogContent>
        </CustomDialog>
    );
};

export default UpdateAccountDialog;
